// src/pages/dashboard/Timesheet/Timesheet.jsx
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import "../../styles/timesheet.css";
import {
  FaCalendarAlt,
  FaPlus,
  FaTimes,
  FaFilter,
  FaRedo,
  FaChevronLeft,
  FaChevronRight,
  FaTasks,
  FaUserPlus,
  FaCheckCircle,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import useTimesheet from "./useTimesheet";

/* ── INLINE PREMIUM POPUP ─────────────────────────────────────── */
const popupStyles = `
@keyframes sp-overlay-in{from{opacity:0}to{opacity:1}}
@keyframes sp-card-in{from{opacity:0;transform:scale(0.55) translateY(28px)}to{opacity:1;transform:scale(1) translateY(0)}}
@keyframes sp-circle-draw{to{stroke-dashoffset:0}}
@keyframes sp-tick-draw{to{stroke-dashoffset:0}}
@keyframes sp-glow-pulse{0%,100%{opacity:.65;transform:scale(1)}50%{opacity:1;transform:scale(1.13)}}
@keyframes sp-burst{0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(var(--tx,20px),var(--ty,-28px)) scale(.2)}}
@keyframes sp-progress{from{transform:scaleX(1)}to{transform:scaleX(0)}}
.sp-overlay{position:fixed;inset:0;background:rgba(8,12,26,.58);backdrop-filter:blur(7px);-webkit-backdrop-filter:blur(7px);z-index:9999999;display:flex;align-items:center;justify-content:center;animation:sp-overlay-in .18s ease}
.sp-card{position:relative;background:linear-gradient(150deg,#fff 0%,#f5f8ff 100%);border-radius:26px;padding:46px 38px 34px;width:350px;max-width:92vw;text-align:center;overflow:hidden;box-shadow:0 0 0 1.5px rgba(255,255,255,.85) inset,0 28px 70px rgba(0,0,0,.17),0 0 55px var(--sp-glow,rgba(34,197,94,.22));animation:sp-card-in .38s cubic-bezier(.34,1.56,.64,1);border:1.5px solid rgba(255,255,255,.65)}
.sp-card::before{content:"";position:absolute;top:0;left:0;right:0;height:4px;background:var(--sp-accent,#22c55e);border-radius:26px 26px 0 0}
.sp-card::after{content:"";position:absolute;inset:0;background:radial-gradient(ellipse at 20% 0%,var(--sp-glow,rgba(34,197,94,.13)) 0%,transparent 60%),radial-gradient(ellipse at 80% 100%,var(--sp-glow,rgba(34,197,94,.08)) 0%,transparent 60%);pointer-events:none;border-radius:26px}
.sp-icon-ring{position:relative;width:88px;height:88px;margin:0 auto 18px}
.sp-icon-glow{position:absolute;inset:-8px;background:var(--sp-glow,rgba(34,197,94,.28));border-radius:50%;filter:blur(13px);animation:sp-glow-pulse 2s ease-in-out infinite}
.sp-svg{position:relative;z-index:1;width:88px;height:88px}
.sp-circle{stroke:var(--sp-accent,#22c55e);stroke-width:2.5;stroke-dasharray:166;stroke-dashoffset:166;stroke-linecap:round;fill:none;animation:sp-circle-draw .6s cubic-bezier(.65,0,.45,1) .1s forwards}
.sp-tick{stroke:var(--sp-accent,#22c55e);stroke-width:3;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:50;stroke-dashoffset:50;fill:none;animation:sp-tick-draw .38s ease .55s forwards}
.sp-sparkles{position:absolute;top:88px;left:50%;transform:translateX(-50%);width:96px;height:96px;pointer-events:none}
.sp-dot{position:absolute;left:50%;top:50%;width:7px;height:7px;border-radius:50%;background:var(--sp-accent,#22c55e);opacity:0}
.sp-d1{animation:sp-burst .65s ease .6s forwards;--tx:-24px;--ty:-30px}
.sp-d2{animation:sp-burst .65s ease .65s forwards;--tx:22px;--ty:-26px}
.sp-d3{animation:sp-burst .65s ease .7s forwards;--tx:-34px;--ty:7px}
.sp-d4{animation:sp-burst .65s ease .72s forwards;--tx:32px;--ty:5px}
.sp-d5{animation:sp-burst .65s ease .68s forwards;--tx:-17px;--ty:26px}
.sp-d6{animation:sp-burst .65s ease .75s forwards;--tx:19px;--ty:24px}
.sp-label{font-family:"Segoe UI","SF Pro Display",system-ui,sans-serif;font-size:21px;font-weight:800;color:#1a202c;letter-spacing:-.3px;margin-bottom:9px;position:relative;z-index:1}
.sp-msg{font-family:"Segoe UI",system-ui,sans-serif;font-size:14px;color:#64748b;line-height:1.6;margin:0 0 26px;position:relative;z-index:1}
.sp-btn{display:inline-flex;align-items:center;gap:8px;background:var(--sp-accent,#22c55e);color:#fff;border:none;border-radius:11px;padding:11px 26px;font-size:14px;font-weight:700;letter-spacing:.3px;cursor:pointer;position:relative;z-index:1;transition:transform .15s,box-shadow .15s;box-shadow:0 6px 18px var(--sp-glow,rgba(34,197,94,.38))}
.sp-btn:hover{transform:translateY(-2px);box-shadow:0 10px 26px var(--sp-glow,rgba(34,197,94,.48))}
.sp-progress{position:absolute;bottom:0;left:0;height:3px;width:100%;background:var(--sp-accent,#22c55e);opacity:.45;border-radius:0 0 26px 26px;transform-origin:left;animation:sp-progress 3.5s linear forwards}
`;

const POPUP_CONFIG = {
  success: { accent: "#22c55e", glow: "rgba(34,197,94,.25)",  label: "Success" },
  error:   { accent: "#ef4444", glow: "rgba(239,68,68,.22)",   label: "Error"   },
  warning: { accent: "#f59e0b", glow: "rgba(245,158,11,.22)",  label: "Warning" },
  info:    { accent: "#3b82f6", glow: "rgba(59,130,246,.22)",  label: "Info"    },
};

function PremiumPopup({ popup, onClose }) {
  useEffect(() => {
    // Inject styles into <head> once
    if (!document.getElementById("sp-styles")) {
      const tag = document.createElement("style");
      tag.id = "sp-styles";
      tag.textContent = popupStyles;
      document.head.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    if (!popup) return;
    if (popup.type === "success" || popup.type === "info") {
      const t = setTimeout(onClose, 3500);
      return () => clearTimeout(t);
    }
  }, [popup, onClose]);

  if (!popup) return null;
  const cfg = POPUP_CONFIG[popup.type] || POPUP_CONFIG.success;
  const isSuccess = popup.type === "success";
  const isError   = popup.type === "error";

  return ReactDOM.createPortal(
    <div className="sp-overlay" onClick={onClose}>
      <div
        className="sp-card"
        style={{ "--sp-accent": cfg.accent, "--sp-glow": cfg.glow }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sp-icon-ring">
          <div className="sp-icon-glow" />
          <svg className="sp-svg" viewBox="0 0 52 52">
            <circle className="sp-circle" cx="26" cy="26" r="25" />
            {isSuccess && <path className="sp-tick" fill="none" d="M14 27l9 9 15-18" />}
            {isError   && <path className="sp-tick" fill="none" strokeWidth="3" d="M17 17 35 35M35 17 17 35" />}
            {!isSuccess && !isError && <path className="sp-tick" fill="none" strokeWidth="3.5" d="M26 16v14M26 35v2" />}
          </svg>
        </div>

        <div className="sp-sparkles">
          <span className="sp-dot sp-d1" /><span className="sp-dot sp-d2" />
          <span className="sp-dot sp-d3" /><span className="sp-dot sp-d4" />
          <span className="sp-dot sp-d5" /><span className="sp-dot sp-d6" />
        </div>

        <div className="sp-label">{popup.title || cfg.label}</div>
        {popup.message && <p className="sp-msg">{popup.message}</p>}

        <button className="sp-btn" onClick={onClose}>
          Got it&nbsp;
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>

        {(popup.type === "success" || popup.type === "info") && (
          <div className="sp-progress" />
        )}
      </div>
    </div>,
    document.body
  );
}
/* ── END INLINE PREMIUM POPUP ─────────────────────────────────── */

export default function Timesheet() {
  const {
    date, setDate,
    tasks, setTasks,
    totalHours,
    history,
    showForm, setShowForm,
    attachments,
    isEditing,
    filterProject, setFilterProject,
    filterPeriod, setFilterPeriod,
    filterValue, setFilterValue,
    currentPage, currentItems, totalPages, goToPrev, goToNext,
    setCurrentPage,
    downloadMonth, setDownloadMonth,
    showMyTasksModal, setShowMyTasksModal,
    assignedTasks,
    loadingMyTasks,
    canAddTimesheet,
    showAssignModal, setShowAssignModal,
    loadingAssignHierarchy,
    showDownloadModal, setShowDownloadModal,
    assignableEmployees,
    selectedEmployee, setSelectedEmployee,
    assignProject, setAssignProject,
    assignClient, setAssignClient,
    assignTaskText, setAssignTaskText,
    assignHours, setAssignHours,
    assignDueDate, setAssignDueDate,
    loadingHistory,
    roleID,
    mongoToDDMM,
    getCalendarType,
    getDefaultValue,
    addTask,
    removeTask,
    updateTask,
    blockSunday,
    handleAttachmentChange,
    startEditTimesheet,
    loadHistory,
    submitTimesheet,
    updateTimesheet,
    deleteTimesheet,
    loadAssignedTasks,
    autoFillTask,
    submitAssignTask,
    downloadMonthlyTimesheet,
    successPopup,
    setSuccessPopup,
  } = useTimesheet();

  const today = new Date().toISOString().split("T")[0];

  // Prevent scroll when any modal is open
  useEffect(() => {
    if (showForm || showMyTasksModal || showAssignModal || showDownloadModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showForm, showMyTasksModal, showAssignModal, showDownloadModal]);

  return (
    <div className="ts-diamond-container">
      {/* Top Action Buttons */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {(roleID === 1 || roleID === 2) && (
          <button
            className="ts-assign-btn"
            onClick={() => setShowAssignModal(true)}
            disabled={loadingAssignHierarchy}
          >
            <FaUserPlus /> Assign Task
          </button>
        )}

        <button
          className="ts-mytasks-btn"
          onClick={() => {
            loadAssignedTasks();
            setShowMyTasksModal(true);
          }}
        >
          <FaTasks /> My Tasks ({assignedTasks.length})
        </button>

        {!showForm && canAddTimesheet && (
          <button className="ts-add-main-btn" onClick={() => setShowForm(true)}>
            <FaPlus /> Add Timesheet
          </button>
        )}

        <button
          className="ts-mytasks-btn"
          onClick={() => setShowDownloadModal(true)}
        >
          📥 Download Monthly
        </button>
      </div>

      {/* ASSIGN TASK MODAL */}
      {showAssignModal && (
        <div className="ts-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>Assign Task</h3>
              <button className="ts-modal-close" onClick={() => setShowAssignModal(false)}>
                <FaTimes />
              </button>
            </div>

            <form className="ts-modal-body" onSubmit={submitAssignTask}>
              {loadingAssignHierarchy && <p>Loading team...</p>}

              {!loadingAssignHierarchy && (
                <>
                  <label>Assign to Employee</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    required
                  >
                    <option value="">-- Select Employee --</option>
                    {assignableEmployees.map((emp) => (
                      <option key={emp.ID} value={emp.empID}>
                        {emp.Name} ({emp.empID})
                      </option>
                    ))}
                  </select>

                  {assignableEmployees.length === 0 && (
                    <p style={{ color: "#ff9800", fontStyle: "italic" }}>
                      No team members found
                    </p>
                  )}
                </>
              )}

              <label>Project</label>
              <input value={assignProject} onChange={(e) => setAssignProject(e.target.value)} required />

              <label>Client</label>
              <input value={assignClient} onChange={(e) => setAssignClient(e.target.value)} required />

              <label>Task Description</label>
              <textarea rows={4} value={assignTaskText} onChange={(e) => setAssignTaskText(e.target.value)} required />

              <label>Expected Hours</label>
              <input type="number" step="0.5" min="0" value={assignHours} onChange={(e) => setAssignHours(e.target.value)} required />

              <label>Due Date</label>
              <input type="date" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)} required />

              <div style={{ marginTop: 20, textAlign: "right" }}>
                <button type="submit" className="ts-submit-btn" disabled={loadingAssignHierarchy}>
                  Assign Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MY TASKS MODAL */}
      {showMyTasksModal && (
        <div className="ts-modal-overlay" onClick={() => setShowMyTasksModal(false)}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>My Assigned Tasks</h3>
              <button className="ts-modal-close" onClick={() => setShowMyTasksModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="ts-modal-body">
              {loadingMyTasks ? (
                <p>Loading...</p>
              ) : assignedTasks.length === 0 ? (
                <p>No tasks assigned yet.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Client</th>
                      <th>Task</th>
                      <th>Due</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedTasks.map((t, i) => (
                      <tr key={i}>
                        <td>{t.project}</td>
                        <td>{t.client}</td>
                        <td>{t.task}</td>
                        <td>{t.dueDate || "-"}</td>
                        <td>
                          <button className="ts-complete-btn" onClick={() => autoFillTask(t)}>
                            <FaCheckCircle /> Use
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DOWNLOAD MONTHLY MODAL */}
      {showDownloadModal && (
        <div className="ts-modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>Download Monthly Timesheet</h3>
              <button className="ts-modal-close" onClick={() => setShowDownloadModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="ts-modal-body">
              <div className="ts-filter-grid">
                <div className="ts-filter-group">
                  <label className="ts-filter-label">Month</label>
                  <input
                    type="month"
                    className="ts-filter-input"
                    value={downloadMonth}
                    onChange={(e) => setDownloadMonth(e.target.value)}
                  />
                </div>

                <div className="ts-filter-actions" style={{ marginTop: 20 }}>
                  <button
                    className="ts-filter-apply"
                    onClick={() => downloadMonthlyTimesheet(downloadMonth)}
                  >
                    Download Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TIMESHEET ENTRY FORM */}
      {showForm && (
        <div className="ts-diamond-card">
          <div className="ts-card-header">
            <h3>{isEditing ? "Edit Timesheet" : "New Timesheet Entry"}</h3>
            <button className="ts-close-btn" onClick={() => setShowForm(false)}>
              <FaTimes />
            </button>
          </div>

          <div className="ts-form-body">
            <label className="ts-form-label">
              <FaCalendarAlt /> Select Date
            </label>
            <input
              type="date"
              className="ts-date-input"
              max={today}
              value={date}
              onChange={(e) => blockSunday(e.target.value)}
            />

            <h4 className="ts-section-title">Tasks</h4>
            {tasks.map((t, i) => (
              <div key={i} className="ts-task-row">
                <input
                  className="ts-task-input"
                  placeholder="Project"
                  value={t.project}
                  onChange={(e) => updateTask(i, "project", e.target.value)}
                />
                <input
                  className="ts-task-input"
                  placeholder="Client"
                  value={t.client}
                  onChange={(e) => updateTask(i, "client", e.target.value)}
                />
                <input
                  className="ts-task-input"
                  placeholder="Task Description"
                  value={t.task}
                  onChange={(e) => updateTask(i, "task", e.target.value)}
                />
                <input
                  type="number"
                  step="0.5"
                  className="ts-task-input hours"
                  placeholder="Hours"
                  value={t.hours}
                  onChange={(e) => updateTask(i, "hours", e.target.value)}
                />
                {tasks.length > 1 && (
                  <button className="ts-task-remove" onClick={() => removeTask(i)}>
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}

            <div className="ts-attachments-block">
              <h4 className="ts-section-title">Attachments</h4>
              <input type="file" multiple className="ts-attachment-input" onChange={handleAttachmentChange} />
              {attachments.length > 0 && (
                <ul className="ts-attachment-list">
                  {attachments.map((f, i) => (
                    <li key={i}>{f.name}</li>
                  ))}
                </ul>
              )}
            </div>

            <button className="ts-add-task-btn" onClick={addTask}>
              <FaPlus /> Add Another Task
            </button>

            <div className="ts-total-hours">
              <span>Total Hours:</span>
              <span className="ts-hours-value">{totalHours.toFixed(1)}</span>
            </div>

            <div className="ts-form-actions">
              <button
                className="ts-submit-btn"
                onClick={isEditing ? updateTimesheet : submitTimesheet}
              >
                {isEditing ? "Update Timesheet" : "Submit Timesheet"}
              </button>
              <button className="ts-cancel-btn" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FILTER CARD */}
      <div className="ts-diamond-card filter-card">
        <div className="ts-card-header">
          <h3>
            <FaFilter /> Filter Timesheets
          </h3>
        </div>

        <div className="ts-filter-grid">
          <div className="ts-filter-group">
            <label className="ts-filter-label">Project</label>
            <input
              className="ts-filter-input"
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              placeholder="e.g. Source Buddy"
            />
          </div>

          <div className="ts-filter-group">
            <label className="ts-filter-label">Period</label>
            <select
              className="ts-filter-select"
              value={filterPeriod}
              onChange={(e) => {
                setFilterPeriod(e.target.value);
                setFilterValue(getDefaultValue(e.target.value));
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="ts-filter-group">
            <label className="ts-filter-label">Value</label>
            <input
              className="ts-filter-input"
              type={getCalendarType(filterPeriod)}
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            />
          </div>

          <div className="ts-filter-actions">
            <button className="ts-filter-apply" onClick={() => loadHistory(false)}>
              Apply
            </button>
            <button
              className="ts-filter-reset"
              onClick={() => {
                setFilterProject("");
                setFilterPeriod("daily");
                setFilterValue(today);
                loadHistory(true);
              }}
            >
              <FaRedo /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* HISTORY TABLE */}
      <div className="ts-diamond-card">
        <div className="ts-card-header">
          <h3>Timesheet History</h3>
          <div className="ts-history-stats">Showing {history.length} record{history.length !== 1 ? "s" : ""}</div>
        </div>

        <div className="ts-history-table-container">
          {loadingHistory ? (
            <div className="ts-empty-state">Loading...</div>
          ) : currentItems.length === 0 ? (
            <div className="ts-empty-state">
              <div className="ts-empty-icon">📊</div>
              <h4>No timesheet data found</h4>
              <p>Submit your first timesheet or adjust filters</p>
            </div>
          ) : (
            <table className="ts-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Task</th>
                  <th>Hours</th>
                  <th>Attachment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((entry, idx) =>
                  (entry.tasks || []).map((task, i) => (
                    <tr key={`${idx}-${i}`} className="ts-history-row">
                      <td className="ts-date-cell">
                        <FaCalendarAlt /> {mongoToDDMM(entry.SelectedDate)}
                      </td>
                      <td>
                        <span className="ts-project-badge">{task.project}</span>
                      </td>
                      <td>{task.client}</td>
                      <td>{task.task}</td>
                      <td>
                        <span className="ts-hours-badge">{task.hours}h</span>
                      </td>
                      <td>
                        {entry.attachments && entry.attachments.length > 0 ? (
                          <button
                            className="ts-attach-btn"
                            onClick={() => {
                              const url = `https://suryojasvi-reflection-api.wyzmindz.com/api/timesheet/attachment?empID=${entry.empID}&date=${entry.SelectedDate.split("T")[0]}&index=0`;
                              const win = window.open("", "_blank");
                              win.document.write(`
                                <html>
                                  <body style="margin:0">
                                    <iframe src="${url}" width="100%" height="100%" style="border:none;"></iframe>
                                  </body>
                                </html>
                              `);
                            }}
                          >
                            📄
                          </button>
                        ) : (
                          <span style={{ color: "#888" }}>None</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <button className="ts-edit-btn" onClick={() => startEditTimesheet(entry)} title="Edit">
                            <FaEdit />
                          </button>
                          <button
                            className="ts-delete-btn"
                            onClick={() => deleteTimesheet(entry.SelectedDate.split("T")[0])}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="ts-pagination">
            <button className="ts-pagination-btn prev" onClick={goToPrev} disabled={currentPage === 1}>
              <FaChevronLeft /> Previous
            </button>

            <div className="ts-page-numbers">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`ts-page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button className="ts-pagination-btn next" onClick={goToNext} disabled={currentPage === totalPages}>
              Next <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* PREMIUM SUCCESS POPUP */}
      <PremiumPopup popup={successPopup} onClose={() => setSuccessPopup(null)} />
    </div>
  );
}
